import { useEffect, useMemo, useState } from 'react'

import { getEmrLinkedResidenUserIds } from 'features/Patients/state/api/emrProviders.transform'
import { useFetchEmrLinkedProvidersQuery } from 'features/Patients/state/api/patientApi'
import { useFetchScheduleDayQuery } from 'features/Schedule/api/schedule.api'
import { scheduleGridQueryOptions } from 'features/Schedule/api/scheduleQueryOptions'

import { doctorAwayKey, isDoctorAwayForVisit } from './doctorAway'

type Visit = {
  staffId: number | undefined
  day: string
  time: Date | string | undefined
  duration: number
}

type Args = {
  open: boolean
  visit: Visit
  siteId: string | undefined
  // The visit the user already agreed to book while away, set on each opening.
  acceptedVisit: Visit | null
}

type DoctorAwayCheck = {
  isConfirmOpen: boolean
  // True when Save must wait for the "Doctor is away" answer; opens the prompt.
  requestConfirm: () => boolean
  // Remembers this visit as agreed, then resumes the save.
  accept: (resume: () => void) => void
  cancel: () => void
}

// Reads the same /schedule/day the calendar uses, for whatever day the form is on.
export const useDoctorAwayCheck = ({
  open,
  siteId,
  visit,
  acceptedVisit,
}: Args): DoctorAwayCheck => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [acceptedKey, setAcceptedKey] = useState<string | null>(null)

  const { currentData: scheduleDay } = useFetchScheduleDayQuery(
    { siteId: siteId ?? '', date: visit.day },
    { skip: !open || !siteId || !visit.day, ...scheduleGridQueryOptions },
  )
  const { data: emrProviders, isSuccess } = useFetchEmrLinkedProvidersQuery(undefined, {
    skip: !open,
  })

  const emrLinkedUserIds = useMemo(
    () => (isSuccess ? getEmrLinkedResidenUserIds(emrProviders ?? []) : null),
    [emrProviders, isSuccess],
  )

  const seedKey = acceptedVisit ? doctorAwayKey(acceptedVisit) : null
  useEffect(() => {
    setIsConfirmOpen(false)
    setAcceptedKey(seedKey)
  }, [open, seedKey])

  const key = doctorAwayKey(visit)
  const isAway = isDoctorAwayForVisit({
    availability: scheduleDay?.availability,
    emrLinkedUserIds,
    staffId: visit.staffId,
    time: visit.time,
    duration: visit.duration,
  })

  const requestConfirm = () => {
    if (!isAway || key === acceptedKey) return false
    setIsConfirmOpen(true)
    return true
  }

  const accept = (resume: () => void) => {
    setAcceptedKey(key)
    setIsConfirmOpen(false)
    resume()
  }

  return { isConfirmOpen, requestConfirm, accept, cancel: () => setIsConfirmOpen(false) }
}
